import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Validation result for image files
 */
export const validateImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPEG, PNG, GIF, or SVG)');
        return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return false;
    }

    return true;
};

/**
 * Hook return type
 */
export interface UseIconUploadReturn {
    file: File | null;
    previewUrl: string;
    inputRef: React.RefObject<HTMLInputElement>;
    handleUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemove: () => void;
}

/**
 * Reusable hook for icon upload functionality
 * Handles file validation, preview generation, and cleanup
 *
 * @param initialUrl - Initial preview URL (optional)
 * @param onUrlChange - Callback when preview URL changes
 * @returns Icon upload utilities and state
 */
export const useIconUpload = (
    initialUrl: string = '',
    onUrlChange?: (url: string) => void
): UseIconUploadReturn => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>(initialUrl);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync previewUrl when initialUrl prop changes (handles saved URLs after navigation)
    useEffect(() => {
        setPreviewUrl(initialUrl);
    }, [initialUrl]);

    const handleUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile && validateImageFile(uploadedFile)) {
            setFile(uploadedFile);

            const reader = new FileReader();
            reader.onload = (e) => {
                const url = e.target?.result as string;
                setPreviewUrl(url);
                if (onUrlChange) {
                    onUrlChange(url);
                }
            };
            reader.readAsDataURL(uploadedFile);
        }
    }, [onUrlChange]);

    const handleRemove = useCallback(() => {
        setFile(null);
        setPreviewUrl('');
        if (onUrlChange) {
            onUrlChange('');
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [onUrlChange]);

    return {
        file,
        previewUrl,
        inputRef,
        handleUpload,
        handleRemove
    };
};
